import React, { useState } from "react";
import InviteFlowStatusBadge from "./InviteFlowStatusBadge";

// SubLinkCard and SubLinksPanel are mutually recursive:
// a card can expand to reveal a nested panel of its own children.

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      type="button"
      className={`sublink-card__copy-btn${copied ? " sublink-card__copy-btn--copied" : ""}`}
      onClick={handleCopy}
      title="Copy manager link"
    >
      {copied ? "Copied!" : "Copy Link"}
    </button>
  );
};

const SubLinkCard = ({ child, depth = 0 }) => {
  const [expanded, setExpanded] = useState(false);

  const {
    inviteSlug,
    invitationName,
    ownerName,
    ownerEmail,
    linkStatus,
    allowSplitting,
    totalSeats,
    usedSeats,
    availableSeats,
    registrationsCount,
    pendingCount,
    approvedCount,
    createdAt,
    managerUrl,
    children = [],
  } = child;

  return (
    <div
      className={`sublink-card${expanded ? " sublink-card--expanded" : ""}`}
      style={{ marginLeft: depth > 0 ? "1.6rem" : 0 }}
    >
      <button
        className="sublink-card__header"
        onClick={() => setExpanded((v) => !v)}
        type="button"
      >
        <div className="sublink-card__header-left">
          <span className="sublink-card__chevron">{expanded ? "▾" : "▸"}</span>
          <div className="sublink-card__identity">
            <span className="sublink-card__name">
              {invitationName || inviteSlug}
            </span>
            <span className="sublink-card__email">{ownerName || ownerEmail}</span>
          </div>
        </div>
        <div className="sublink-card__header-right">
          <InviteFlowStatusBadge status={linkStatus} />
          <span className="sublink-card__seat-pill">
            {availableSeats}/{totalSeats} seats
          </span>
          {pendingCount > 0 && (
            <span className="sublink-card__pending-pill">
              {pendingCount} pending
            </span>
          )}
          {children.length > 0 && (
            <span className="sublink-card__children-pill">
              {children.length} sub-link{children.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      </button>

      {expanded && (
        <div className="sublink-card__body">
          <div className="sublink-card__stats">
            <div className="sublink-card__stat">
              <span className="sublink-card__stat-label">Total</span>
              <span className="sublink-card__stat-value">{totalSeats}</span>
            </div>
            <div className="sublink-card__stat">
              <span className="sublink-card__stat-label">Used</span>
              <span className="sublink-card__stat-value">{usedSeats}</span>
            </div>
            <div className="sublink-card__stat">
              <span className="sublink-card__stat-label">Available</span>
              <span className="sublink-card__stat-value sublink-card__stat-value--highlight">
                {availableSeats}
              </span>
            </div>
            <div className="sublink-card__stat">
              <span className="sublink-card__stat-label">Registrations</span>
              <span className="sublink-card__stat-value">{registrationsCount}</span>
            </div>
            <div className="sublink-card__stat">
              <span className="sublink-card__stat-label">Approved</span>
              <span className="sublink-card__stat-value sublink-card__stat-value--approved">
                {approvedCount}
              </span>
            </div>
            <div className="sublink-card__stat">
              <span className="sublink-card__stat-label">Pending</span>
              <span className="sublink-card__stat-value sublink-card__stat-value--pending">
                {pendingCount}
              </span>
            </div>
          </div>

          {managerUrl && (
            <div className="sublink-card__manager-link">
              <span className="sublink-card__manager-link-label">Manager Link</span>
              <div className="sublink-card__manager-link-row">
                <span className="sublink-card__manager-link-url">{managerUrl}</span>
                <CopyButton text={managerUrl} />
              </div>
            </div>
          )}

          <div className="sublink-card__meta">
            <span className="sublink-card__meta-item">
              Slug: <code>{inviteSlug}</code>
            </span>
            {allowSplitting && (
              <span className="sublink-card__meta-item sublink-card__meta-item--tag">
                Can split
              </span>
            )}
            {createdAt && (
              <span className="sublink-card__meta-item">
                Created{" "}
                {new Date(createdAt).toLocaleDateString("en-US", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </span>
            )}
          </div>

          {/* Recursive: render this child's own sub-links */}
          {children.length > 0 && (
            <div className="sublink-card__nested">
              <SubLinksPanel children={children} depth={depth + 1} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SubLinksPanel = ({ children, depth = 0 }) => {
  const [open, setOpen] = useState(depth === 0);

  if (!children || children.length === 0) return null;

  return (
    <div className={`sublinks-panel${depth > 0 ? " sublinks-panel--nested" : ""}`}>
      <button
        className="sublinks-panel__toggle"
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <span>{open ? "▾" : "▸"}</span>
        <span>Sub-Links</span>
        <span className="sublinks-panel__count">{children.length}</span>
      </button>

      {open && (
        <div className="sublinks-panel__list">
          {children.map((child) => (
            <SubLinkCard key={child.inviteSlug} child={child} depth={depth} />
          ))}
        </div>
      )}
    </div>
  );
};

export default SubLinksPanel;
